import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { stockSelector } from 'store/stock/selectors';

import { ModalInstruments } from 'components/ModalInstruments';
import { ButtonLabel } from 'components/Button';

import { TableExitInstruments } from './TableExitInstruments';
import { TableExitService } from './TableExitService';

const subTypes = [
  {
    key: 'instrument',
    name: 'Инструменты',
    childs: ['Инструмент для оренди', 'Риштування для оренди'],
  },
  {
    key: 'consumables',
    name: 'Расходники',
    childs: ['Видаткові матеріали для оренди'],
  },
  {
    key: 'service',
    name: 'Услуги',
    childs: ['Послуга', 'Послуги'],
  },
];

const RentExitTable = ({ rateVAT, canChange, instrumentsList, onRemove, onUpdate, isFullPledge, setIsFullPledge }) => {
  const stock = useSelector(stockSelector);

  const [openInst, setOpenInst] = useState([]);
  const [calculator, setCalculator] = useState(null);
  const [instrumentModal, setInstrumentModal] = useState(null);
  const [activeType, setActiveType] = useState(subTypes[0]);

  const handleSelect = (type) => {
    setActiveType(type);
  };

  const handleRemove = (instrumentId) => {
    if (onRemove && instrumentId) {
      const findIndex = openInst.find((inst) => inst.id === instrumentId);

      if (findIndex !== -1) {
        const newArray = openInst.slice();
        newArray.splice(findIndex, 1);

        setOpenInst(newArray);
      }

      onRemove(instrumentId);
    }
  };

  const handleUpdateComponent = (instrument, componentId, value) => {
    if (instrument && instrument.components) {
      const componentIndex = instrument.components.findIndex((comp) => comp.id === componentId);

      if (componentIndex !== -1) {
        const newComponents = instrument.components.slice();
        newComponents[componentIndex] = { ...newComponents[componentIndex], quantity: value };

        onUpdate(instrument.id, { ...instrument, components: newComponents });
      }
    }
  };

  const handleAddComponent = (instrument) => {
    setInstrumentModal(instrument);
  };

  const handleRemoveComponent = (instrument, componentId) => {
    if (instrument && instrument.components) {
      const componentIndex = instrument.components.findIndex((comp) => comp.id === componentId);

      if (componentIndex !== -1) {
        const newComponents = instrument.components.slice();
        newComponents.splice(componentIndex, 1);
        onUpdate(instrument.id, { ...instrument, components: newComponents });
      }
    }
  };

  const handleCalculator = (item) => {
    setCalculator(item);
  };

  const addInstrumentComponent = (value) => {
    setInstrumentModal(null);

    if (!instrumentModal || !instrumentModal.components) {
      return;
    }

    const newComponents = instrumentModal.components.slice();

    newComponents.push({
      id: value.id,
      itemID1C: value.itemID,
      quantity: 1,
      title: value.title,
      category: value.category,
      type: value.type,
      subtype: value.subtype,
    });

    onUpdate(instrumentModal.id, { ...instrumentModal, components: newComponents });
  };

  const handleOpen = (inst) => {
    if (inst.components) {
      const newArray = openInst.slice();

      if (openInst.includes(inst.id)) {
        const index = newArray.findIndex((item) => item === inst.id);

        if (index !== -1) {
          newArray.splice(index, 1);
        }
      } else {
        newArray.push(inst.id);
      }

      setOpenInst(newArray);
    }
  };

  const filterInst = useMemo(() => {
    const list = {};

    subTypes.forEach(({ key, childs }) => {
      if (!list[key]) {
        list[key] = [];
      }

      if (instrumentsList) {
        list[key] = instrumentsList.filter((inst) => childs.includes(inst.subtype));
      }
    });

    return list;
  }, [instrumentsList, subTypes]);

  const rentSum = useMemo(() => {
    let tax = 0;
    let pledge = 0;
    let itemSum = 0;
    let sum = 0;
    let itemSumOUT = 0;
    let surchargeAmount = 0;
    let refundAmount = 0;

    if (activeType) {
      // Считаем суммы инструментов
      filterInst[activeType.key].forEach((inst) => {
        tax += ((inst.itemRentSum * inst.quantity) / 100) * rateVAT;
        sum += inst.itemRentSum * inst.quantity + ((inst.itemRentSum * inst.quantityOUT) / 100) * rateVAT;
        itemSum += Number(inst.itemRentSum);
        itemSumOUT += Number(inst.itemRentSum * inst.quantityOUT);
        pledge += Number(inst.pledge * inst.quantityOUT);
        surchargeAmount += Number(inst.surcharge_amount * inst.quantityOUT);
        refundAmount += Number(inst.refund_amount * inst.quantityOUT);
      });
    }

    return {
      tax,
      sum,
      itemSum,
      pledge,
      surchargeAmount,
      refundAmount,
      itemSumOUT,
    };
  }, [rateVAT, filterInst, activeType]);

  return (
    <>
      <div className="table-transfer">
        <div className="table-wrapper">
          <div className="table-action-buttons">
            {subTypes
              .filter((subtype) => canChange || filterInst[subtype.key].length > 0)
              .map((subtype) => (
                <ButtonLabel
                  key={subtype.key}
                  onClick={() => handleSelect(subtype)}
                  active={subtype.name === activeType?.name}
                  text={subtype.name}
                />
              ))}
            <div className="title title-table">Заказ</div>
          </div>

          {activeType?.key === 'instrument' && (
            <TableExitInstruments
              data={activeType && filterInst[activeType.key]}
              opened={openInst}
              canChange={canChange}
              rentSum={rentSum}
              rateVAT={rateVAT}
              calculator={calculator}
              onUpdate={onUpdate}
              handleCalculator={handleCalculator}
              handleOpen={handleOpen}
              handleRemove={handleRemove}
              handleUpdateComponent={handleUpdateComponent}
              handleAddComponent={handleAddComponent}
              handleRemoveComponent={handleRemoveComponent}
              handleChangeCalculator={(newInst) => {
                setCalculator(null);
                onUpdate(newInst.id, newInst);
              }}
              isFullPledge={isFullPledge}
              setIsFullPledge={setIsFullPledge}
            />
          )}

          {(activeType?.key === 'service' || activeType?.key === 'consumables') && (
            <TableExitService
              data={activeType && filterInst[activeType.key]}
              opened={openInst}
              canChange={canChange}
              rentSum={rentSum}
              rateVAT={rateVAT}
              onUpdate={onUpdate}
              handleOpen={handleOpen}
              handleRemove={handleRemove}
              handleUpdateComponent={handleUpdateComponent}
              handleAddComponent={handleAddComponent}
              handleRemoveComponent={handleRemoveComponent}
            />
          )}

          {instrumentModal && (
            <ModalInstruments
              selected={[]}
              А
              defaultStock={stock || null}
              onAdd={addInstrumentComponent}
              onClose={() => setInstrumentModal(null)}
            />
          )}
        </div>
      </div>
    </>
  );
};

RentExitTable.propTypes = {
  instrumentsList: PropTypes.array,
  onRemove: PropTypes.func,
  onUpdate: PropTypes.func,
  canChange: PropTypes.bool,
  rateVAT: PropTypes.number,
  isFullPledge: PropTypes.bool,
  setIsFullPledge: PropTypes.func,
};

RentExitTable.defaultProps = {
  instrumentsList: [],
  onUpdate: () => {},
  onRemove: () => {},
  canChange: true,
  rateVAT: 0,
};

export { RentExitTable };
